import React, {
  CSSProperties,
  Component,
  SyntheticEvent,
  KeyboardEvent,
  FC,
  MouseEvent,
  InputHTMLAttributes,
  useRef,
  useCallback,
  useEffect,
} from "react";
import Draggable, { DraggableEventHandler } from "react-draggable";
import { GridProps, Grid } from "react-virtualized";
import { useDrag, useDrop } from "react-dnd";
import classNames from "classnames";

import memoize from "memoizee";
import { Item } from "types/Component";
import { Fa } from "composants/Icon";
import { TypeSimpleComponent } from "enum";
import { useTranslation } from "react-i18next";

interface DragHandleProps {
  label: string;
  tooltip?: string;
  required: boolean;
}
export const DragHandle: FC<DragHandleProps> = ({ label, tooltip, required }: DragHandleProps) => (
  <label className="datatable-header-cell--label" data-required={required}>
    {tooltip ? <abbr title={tooltip}>{label}</abbr> : label}
  </label>
);

const computeStyle = memoize((args: object) => ({ ...args }), {
  length: false,
  primitive: true,
  normalizer: function (...args: any[]) {
    return JSON.stringify(args);
  },
});

export interface SortableHeaderItemProps {
  column: string;
  typeCompo: string;
  label: string;
  filterOpen: boolean;
  tooltip?: string;
  style: CSSProperties;
  required: boolean;
  children?: React.ReactNode;
  sort?: "ASC" | "DESC" | "NONE";
  onResize?: DraggableEventHandler;
  onSortAsc?(e: MouseEvent<HTMLAnchorElement>): void;
  onSortDesc?(e: MouseEvent<HTMLAnchorElement>): void;
}

export const SortableHeaderItem = React.forwardRef<HTMLDivElement, SortableHeaderItemProps>(
  (
    {
      column,
      typeCompo,
      label,
      tooltip,
      style,
      required,
      children,
      sort = "NONE",
      onResize,
      onSortAsc,
      onSortDesc,
      filterOpen,
      ...props
    },
    ref,
  ) => {
    const resizeHandleRef = useRef<HTMLSpanElement | null>(null);

    return (
      <div
        ref={ref}
        className="datatable-header-cell has-text-weight-semibold"
        data-filter-active={filterOpen}
        style={style}
        {...props}
      >
        <div className="datatable-header-cell--title" style={computeStyle({ width: style.width })}>
          {onResize ? (
            <Draggable
              axis="x"
              onDrag={onResize}
              position={{ x: 0, y: 0 }}
              nodeRef={resizeHandleRef}
            >
              <span
                ref={resizeHandleRef}
                className="datatable-header-cell--resize"
                style={{ paddingRight: 5 }}
              >
                ⋮
              </span>
            </Draggable>
          ) : null}
          <DragHandle label={label} tooltip={tooltip} required={required} />
          {typeCompo !== TypeSimpleComponent.OEL && (
            <div className="sort-container" style={{ marginRight: 5 }}>
              <a
                style={{
                  color: sort === "ASC" ? "red" : undefined,
                }}
                onClick={onSortAsc}
              >
                <Fa icon="sort-up" />
              </a>
              <a
                style={{
                  color: sort === "DESC" ? "red" : undefined,
                }}
                onClick={onSortDesc}
              >
                <Fa icon="sort-down" />
              </a>
            </div>
          )}
        </div>
        {children}
      </div>
    );
  },
);

export const COLUMN_DRAG_TYPE = "DATATABLE_COLUMN";

export type ColumnDragItem = { index: number; sourceType: "datatable" | "ir" };

export function useDraggableColumn(
  columnIndex: number,
  sourceType: ColumnDragItem["sourceType"],
  onReorder: (oldIndex: number, newIndex: number) => void,
) {
  const nodeRef = useRef<HTMLElement | null>(null);
  const lastMouseDownTargetRef = useRef<EventTarget | null>(null);

  const [{ isDragging }, drag] = useDrag({
    type: COLUMN_DRAG_TYPE,
    item: { index: columnIndex, sourceType } satisfies ColumnDragItem,
    canDrag: () => {
      const target = lastMouseDownTargetRef.current;
      return !(target instanceof Element && target.closest(".datatable-header-cell--resize"));
    },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const [{ isOver }, drop] = useDrop<ColumnDragItem, void, { isOver: boolean }>({
    accept: COLUMN_DRAG_TYPE,
    collect: (monitor) => ({ isOver: monitor.isOver() }),
    drop: (item) => {
      if (item.sourceType === sourceType && item.index !== columnIndex) {
        onReorder(item.index, columnIndex);
      }
    },
  });

  const refCallback = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node;
      drag(node);
      drop(node);
    },
    [drag, drop],
  );

  // Attache les listeners natifs sur le nœud pour bloquer le drag depuis le resize handle.
  // useEffect avec [] : nodeRef.current est garanti non-null (React assigne les refs avant useEffect).
  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    const trackMouseDown = (e: Event) => {
      lastMouseDownTargetRef.current = e.target;
    };

    node.addEventListener("mousedown", trackMouseDown, true);

    return () => {
      node.removeEventListener("mousedown", trackMouseDown, true);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dragStyle = useCallback(
    (style: CSSProperties): CSSProperties => ({
      ...style,
      opacity: isDragging ? 0.4 : 1,
      cursor: "grab",
      border: isOver ? "2px dotted #3273dc" : undefined,
    }),
    [isDragging, isOver],
  );

  return { refCallback, dragStyle };
}

export interface DraggableSortableHeaderItemProps extends SortableHeaderItemProps {
  columnIndex: number;
  onReorder: (oldIndex: number, newIndex: number) => void;
}

export const DraggableSortableHeaderItem: FC<DraggableSortableHeaderItemProps> = ({
  columnIndex,
  onReorder,
  style,
  ...rest
}) => {
  const { refCallback, dragStyle } = useDraggableColumn(columnIndex, "datatable", onReorder);

  return (
    <SortableHeaderItem
      ref={refCallback as React.Ref<HTMLDivElement>}
      style={dragStyle(style)}
      {...rest}
    />
  );
};

interface ComponentFilterProps {
  name: string;
  filterOpen: boolean;
  filterValue: any;
  onFilterChange(e: SyntheticEvent<any>): void;
  onKeyDown(e: KeyboardEvent<HTMLInputElement>): void;
}

export const ComponentFilterInput: FC<
  ComponentFilterProps & InputHTMLAttributes<HTMLInputElement>
> = ({ name, filterOpen, filterValue = "", onFilterChange, onKeyDown, ...props }) => {
  return (
    <input
      name={name}
      className="input is-small"
      style={computeStyle({
        width: "100%",
        display: filterOpen ? "block" : "none",
      })}
      value={filterValue}
      onChange={onFilterChange}
      onKeyDown={onKeyDown}
      {...props}
    />
  );
};

export const ComponentFilterSelect: FC<ComponentFilterProps & { options: Item[] }> = ({
  name,
  filterValue = "",
  onFilterChange,
  onKeyDown,
  filterOpen,
  options,
}) => {
  return (
    <div
      className="select is-small"
      style={computeStyle({
        display: filterOpen ? "block" : "none",
      })}
    >
      <select name={name} value={filterValue} onChange={onFilterChange}>
        <option />
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export const ComponentFilterCheckbox: FC<ComponentFilterProps> = ({
  name,
  filterOpen,
  filterValue,
  onFilterChange,
}) => {
  const [t] = useTranslation();

  return (
    <div
      className="select is-small"
      style={computeStyle({
        display: filterOpen ? "block" : "none",
      })}
    >
      <select name={name} value={filterValue} onChange={onFilterChange}>
        <option />
        <option value="true">{t("commun_oui")}</option>
        <option value="false">{t("commun_non")}</option>
      </select>
    </div>
  );
};

export const SortableHeaderContainer: FC<GridProps & { innerRef?: (ref: Grid | null) => void }> = ({
  innerRef,
  className,
  ...restProps
}) => {
  return (
    <Grid
      ref={innerRef}
      className={classNames("datatable-header", className)}
      {...(restProps as GridProps)}
    />
  );
};
